// ============================================================
// phoneLinks.ts — turn a typed phone number into working CALL + WHATSAPP
// links.
//
// Why: the call strip linked `tel:${phoneValue}` with the number exactly as
// typed ("07700 900 418" — spaces, national 0-prefix). On a machine where
// WhatsApp Desktop is the registered tel: handler that opened WhatsApp but
// NOT the contact's chat (the raw national number can't be resolved to a
// WhatsApp account). WhatsApp's own deep link is https://wa.me/<number> with
// the number in international form, DIGITS ONLY (no +, spaces, brackets or
// leading zeros) — that opens the specific conversation.
//
// normalisePhone() → international digits (E.164 without the "+"):
//   "07700 900001"        → "447700900001"   (UK national → +44)
//   "+44 (0)7700 900001"  → "447700900001"   (stray (0) trunk prefix dropped)
//   "0044 7700 900001"    → "447700900001"   (00 international prefix)
//   "+1 (415) 555-0100"   → "14155550100"    (non-UK: country code kept)
//   "7700 900001"         → "447700900001"   (bare UK mobile, no 0)
//   "", "abc", "123"      → null             (no / invalid number)
// Default country is the UK (Casevo is UK practice software); any number
// typed with a + or 00 keeps its own country code.
// ============================================================

const DEFAULT_COUNTRY_CODE = '44'

export const normalisePhone = (raw: string | null | undefined, countryCode = DEFAULT_COUNTRY_CODE): string | null => {
  if (!raw) return null
  // ★ (Audit) Cut off an extension ("… ext 2", "x2", "#2", "ext.2") before
  // stripping non-digits — it was glued onto the number, dialling a wrong one.
  const trimmed = raw.trim().replace(/\s*(?:ext\.?|extension|x|#)\s*\d+\s*$/i, '').trim()
  if (!trimmed) return null
  // International when it starts with + or 00; otherwise national.
  const plus = trimmed.startsWith('+')
  let digits = trimmed.replace(/\D/g, '')
  if (!digits) return null
  let international: string
  if (plus) {
    international = digits
  } else if (digits.startsWith('00')) {
    international = digits.slice(2)
  } else if (digits.startsWith('0')) {
    // National trunk prefix (UK 07…, 020…) → country code + the rest.
    international = countryCode + digits.replace(/^0+/, '')
  } else if (digits.startsWith(countryCode) && digits.length >= countryCode.length + 9) {
    // Already international, typed without the + ("447700900001").
    international = digits
  } else {
    // Bare national significant number ("7700 900001").
    international = countryCode + digits
  }
  // "+44 (0)7700…" → the bracketed trunk 0 survives as "440…" — drop it.
  if (international.startsWith(countryCode + '0')) {
    international = countryCode + international.slice(countryCode.length).replace(/^0+/, '')
  }
  digits = international
  // E.164: at most 15 digits, and a country code never starts with 0 (e.g. a
  // typed "+0 7700…"); anything under 8 can't be a real subscriber number —
  // don't build a broken link.
  if (digits.length < 8 || digits.length > 15 || digits.startsWith('0')) return null
  return digits
}

/** tel: link in E.164 ("tel:+447700900001"), or null for no/invalid number. */
export const telHrefOf = (raw: string | null | undefined): string | null => {
  const n = normalisePhone(raw)
  return n ? `tel:+${n}` : null
}

/** WhatsApp chat link for THIS number ("https://wa.me/447700900001"), or null. */
export const whatsAppHrefOf = (raw: string | null | undefined): string | null => {
  const n = normalisePhone(raw)
  return n ? `https://wa.me/${n}` : null
}
